import React, { useEffect, useState, useCallback } from 'react';
import { DollarSign, Plus, Pencil, Trash2 } from 'lucide-react';
import { useOrgId } from '../../hooks/useOrgId';
import { useT } from '../../i18n/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { SchoolAccountSearch } from '../../components/school/SchoolAccountSearch';
import { toast } from 'sonner';

export default function FeesGroupsPage() {
  const orgId = useOrgId();
  const { t } = useT();
  const { checkActionPermission } = useAuth();
  const canAdd = checkActionPermission('/school/fees/groups', 'add');
  const canEditGroups = checkActionPermission('/school/fees/groups', 'edit');
  const canDeleteGroups = checkActionPermission('/school/fees/groups', 'delete');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // group or {}
  const [form, setForm] = useState({ name: '', description: '', revenue_account_code: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try { setRows(await schoolApi.listFeeGroups(orgId)); } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: '', description: '', revenue_account_code: '' }); setDialog({}); };
  const openEdit = (g) => { setForm({ name: g.name || '', description: g.description || '', revenue_account_code: g.revenue_account_code || '' }); setDialog(g); };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('common.required', 'Name required')); return; }
    setSaving(true);
    try {
      const body = { organization_id: orgId, ...form };
      if (dialog?.id) await schoolApi.updateFeeGroup(dialog.id, body);
      else await schoolApi.createFeeGroup(body);
      toast.success(t('common.saved', 'Saved')); setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    try { await schoolApi.deleteFeeGroup(g.id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="fees-groups-page">
      <PageHeader icon={DollarSign} title={t('nav.feesGroups', 'Fees Groups')} subtitle="Reusable fee categories mapped to revenue accounts"
        actions={canAdd ? <Button onClick={openNew} data-testid="fee-group-new"><Plus className="w-4 h-4 mr-1" /> New Group</Button> : null} />
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState icon={DollarSign} title="No fee groups yet" description="Create categories like Scolarite, Inscription" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((g) => (
            <Card key={g.id} className="p-4" data-testid={`fee-group-${g.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{g.name}</p>
                  {g.description && <p className="text-xs text-muted-foreground mt-0.5">{g.description}</p>}
                  <p className="text-xs mt-2">Revenue account: <span className="font-mono">{g.revenue_account_code || '\u2014'}</span></p>
                </div>
                <div className="flex gap-1">
                  {canEditGroups && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(g)} data-testid={`fee-group-edit-${g.id}`}><Pencil className="w-4 h-4" /></Button>}
                  {canDeleteGroups && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => remove(g)} data-testid={`fee-group-del-${g.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent data-testid="fee-group-dialog">
          <DialogHeader><DialogTitle>{dialog?.id ? 'Edit' : 'New'} Fee Group</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Scolarite" data-testid="fee-group-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>Revenue account code</Label><SchoolAccountSearch orgId={orgId} value={form.revenue_account_code} onChange={(v) => setForm({ ...form, revenue_account_code: v })} placeholder="e.g. 70110000" testid="fee-group-account" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="fee-group-save">{saving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
